import { Path } from 'slate';
import { defineTransform } from '../transform';

import {
  adjustPath,
  pathWasRemoved,
  adjustNodePathForNodeMove,
  adjustPathForNodeSplit,
  adjustPathForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
} from '../utils';

defineTransform('merge_node', 'insert_text', (op1, op2, side) => {
  const targetNodePath = adjustPath(op1.path, -1, op1.path.length - 1);
  if (Path.equals(targetNodePath, op2.path)) {
    return {
      ...op1,
      position: op1.position + op2.text.length,
    };
  }
  return op1;
});

defineTransform('merge_node', 'remove_text', (op1, op2, side) => {
  const targetNodePath = adjustPath(op1.path, -1, op1.path.length - 1);
  if (Path.equals(targetNodePath, op2.path)) {
    return {
      ...op1,
      position: op1.position - op2.text.length,
    };
  }
  return op1;
});

defineTransform('merge_node', 'split_node', (op1, op2, side) => {
  const tOp = {
    ...op1,
    // Position is -1, because a split on our node should never affect
    // our merge. We always want to lose the tiebreaker. We merge
    // whatever nodes we have left after the split.
    path: adjustPathForNodeSplit(op1.path, -1, op2, true),
  };

  const targetPath = adjustPath(op1.path, -1, op1.path.length - 1);

  if (Path.equals(targetPath, op2.path)) {
    // If we're splitting the node we're merging into, adjust our position to
    // the number of nodes in the newly split node.
    tOp.position = tOp.position - op2.position;
  }

  if (Path.equals(targetPath, op2.path.slice(0, -1))) {
    // If we're splitting a child of the node we're merging into, we
    // have to adjust our position to account for the added node.
    tOp.position = tOp.position + 1;
  }

  if (tOp.path[tOp.path.length - 1] === 0) {
    // We have no node to merge into, so we need to move the node into
    // the other tree.
    const previousNodePath = adjustPath(op1.path, -1, op1.path.length - 1);
    const moveOperation = {
      type: 'move_node',
      path: previousNodePath,
      newPath: tOp.path,
    };

    tOp.path = adjustNodePathForNodeMove(tOp.path, moveOperation);
    return [moveOperation, tOp];
  }

  return tOp;
});

defineTransform('merge_node', 'merge_node', (op1, op2, side) => {
  if (Path.equals(op1.path, op2.path)) return;

  const tOp = {
    ...op1,
    path: adjustPathForNodeMerge(op1.path, op2),
  };

  const parentLength = op2.path.length;

  if (Path.equals(op1.path, adjustPath(op2.path, 1, parentLength - 1))) {
    // We merged the sibling before ours, so we need to adjust for the new nodes.
    tOp.position = tOp.position + op2.position;
  }

  if (
    Path.equals(
      op1.path,
      adjustPath(op2.path.slice(0, -1), 1, parentLength - 2)
    )
  ) {
    // We merged a child of the node we're merging into, so there's
    // one fewer node over there than there used to be.
    tOp.position = tOp.position - 1;
  }

  return tOp;
});

defineTransform('merge_node', 'move_node', (op1, op2, side) => {
  // if it's a no-op move, our merge doesn't need to change.
  if (Path.equals(op2.path, op2.newPath)) return op1;

  const tOp = {
    ...op1,
    path: adjustNodePathForNodeMove(op1.path, op2),
  };

  const previousNodePath = adjustPath(op1.path, -1, op1.path.length - 1);

  if (Path.equals(op1.path, op2.path)) {
    // Move should dominate merge, so we can ignore the merge. On the
    // other side the node will be split, then moved.
    return [];
  }

  if (Path.equals(previousNodePath, op2.path)) {
    // If the node we're merging into has been moved, we need to move alongside it, so the merge still has the same effect.

    const newPath = adjustPathForNodeMerge(op2.newPath, op1);
    const moveOperation = {
      type: 'move_node',
      path: tOp.path,
      newPath: adjustPath(newPath, 1, newPath.length - 1),
    };

    tOp.path = adjustNodePathForNodeMove(tOp.path, moveOperation);
    return [moveOperation, tOp];
  }

  if (Path.equals(op1.path, op2.newPath)) {
    // if a node has been inserted between our node and the node we're
    // merging into, move it so it won't merge into the newly inserted
    // node.
    const moveOperation = {
      type: 'move_node',
      path: tOp.path,
      newPath: op1.path,
    };

    tOp.path = moveOperation.newPath;
    return [moveOperation, tOp];
  }

  // If we moved one of our previous siblings' child nodes, we need to
  // adjust the position for the now missing child.
  if (
    Path.isAncestor(previousNodePath, op2.path) &&
    op2.path.length === op1.path.length + 1
  ) {
    tOp.position = op1.position - 1;
  }

  if (
    Path.isAncestor(previousNodePath, op2.newPath) &&
    op2.newPath.length === op1.path.length + 1
  ) {
    tOp.position = op1.position + 1;
  }

  return tOp;
});

defineTransform('merge_node', 'insert_node', (op1, op2, side) => {
  const tOp = {
    ...op1,
    path: adjustPathForNodeInsert(op1.path, op2, false),
  };

  // If we inserted a node between our node and the one we merged
  // into, move our node before the newly inserted node so we can
  // perform our original merge.
  if (Path.equals(op1.path, op2.path)) {
    const moveNodeOperation = {
      type: 'move_node',
      path: adjustPath(op1.path, 1, op1.path.length - 1),
      newPath: tOp.path,
    };

    return [moveNodeOperation, tOp];
  }

  const targetNodePath = adjustPath(op1.path, -1, op1.path.length - 1);
  if (
    op2.path.length === targetNodePath.length + 1 &&
    Path.isAncestor(targetNodePath, op2.path)
  ) {
    // We added a child to the node we're merging into, so we need to
    // adjust the position to match.
    tOp.position = tOp.position + 1;
  }

  return tOp;
});

defineTransform('merge_node', 'remove_node', (op1, op2, side) => {
  const tOp = { ...op1 };
  const targetNodePath = adjustPath(op1.path, -1, op1.path.length - 1);

  if (
    pathWasRemoved(op1.path, op2.path) ||
    pathWasRemoved(targetNodePath, op2.path)
  )
    return;

  if (
    op2.path.length === targetNodePath.length + 1 &&
    Path.isAncestor(targetNodePath, op2.path)
  ) {
    // We added a child to the node we're merging into, so we need to
    // adjust the position to match.
    tOp.position = tOp.position - 1;
  }

  tOp.path = adjustPathForNodeRemove(op1.path, op2, false);

  return tOp;
});

defineTransform('merge_node', 'set_node', (op1, op2, side) => op1);
defineTransform('merge_node', 'add_mark', (op1, op2, side) => op1);
defineTransform('merge_node', 'remove_mark', (op1, op2, side) => op1);
defineTransform('merge_node', 'set_mark', (op1, op2, side) => op1);
defineTransform('merge_node', 'add_annotation', (op1, op2, side) => op1);
defineTransform('merge_node', 'remove_annotation', (op1, op2, side) => op1);
defineTransform('merge_node', 'set_annotation', (op1, op2, side) => op1);
defineTransform('merge_node', 'set_selection', (op1, op2, side) => op1);
defineTransform('merge_node', 'add_metadata', (op1, op2, side) => op1);
defineTransform('merge_node', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('merge_node', 'set_metadata', (op1, op2, side) => op1);
